import React, { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { useIsMobile } from '@/lib/useIsMobile'
import { formatCurrency, formatDate, formatRelativeDate, getStatusColor } from '@/lib/utils'
import { contactSnapshot } from '@/lib/contactSnapshot'
import { useFeature, useLimitCheck } from '@/lib/billing'
import { Plus, Search, X, FileText, DollarSign, AlertTriangle, CheckCircle2, Clock, TrendingUp, UserPlus } from 'lucide-react'
import { toast } from 'sonner'

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  draft:   { label: 'Draft',   color: 'var(--ink-tertiary)', bg: 'var(--border-subtle)' },
  sent:    { label: 'Sent',    color: 'var(--info)',         bg: 'var(--info-dim)' },
  viewed:  { label: 'Viewed',  color: 'var(--accent-text)',  bg: 'var(--accent-dim)' },
  paid:    { label: 'Paid',    color: 'var(--positive)',     bg: 'var(--positive-dim)' },
  partial: { label: 'Partial', color: 'var(--warning)',      bg: 'var(--warning-dim)' },
  overdue: { label: 'Overdue', color: 'var(--danger)',       bg: 'var(--danger-dim)' },
  void:    { label: 'Void',    color: 'var(--ink-tertiary)', bg: 'var(--hover-bg)' },
}

function NewInvoiceModal({ onClose, onCreated, contacts }: { onClose: () => void; onCreated: (id: string) => void; contacts: any[] }) {
  const { workspace } = useAuth()
  const queryClient = useQueryClient()
  // Contacts feature + limit gates. contactCount comes from the parent's
  // contacts query (already passed as a prop for the dropdown) — no extra
  // round trip. atLimit/feature surface as soft UI gates here; the DB-side
  // RLS + plan limits remain the final backstop on the insert.
  const hasContactsFeature = useFeature('contacts')
  const { atLimit, limit, isLoading: limitLoading } = useLimitCheck('contacts', contacts.length)
  const canQuickCreate = hasContactsFeature && !atLimit && !limitLoading

  const [form, setForm] = useState({ contact_id: '', contact_name: '', company_name: '', contact_email: '', due_date: '', notes: '' })
  const [saving, setSaving] = useState(false)
  const set = (k: string, v: string) => setForm(p => ({ ...p, [k]: v }))
  const inp: React.CSSProperties = { width: '100%', padding: '9px 12px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box' }
  const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }

  // ─── Inline quick-create customer state ──────────────────────────────────
  const [showQuickCreate, setShowQuickCreate] = useState(false)
  const [qcForm, setQcForm] = useState({ first_name: '', last_name: '', email: '', company_name: '' })
  const [qcSaving, setQcSaving] = useState(false)
  const qcSet = (k: keyof typeof qcForm, v: string) => setQcForm(p => ({ ...p, [k]: v }))

  const handleContactSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const contact = contacts.find(c => c.id === e.target.value)
    if (!contact) {
      setForm(p => ({ ...p, contact_id: '', contact_name: '', company_name: '', contact_email: '' }))
      return
    }
    const snap = contactSnapshot(contact)
    setForm(p => ({
      ...p,
      contact_id: contact.id,
      contact_name: snap.contact_name,
      company_name: snap.company_name ?? '',
      contact_email: contact.email || '',
    }))
  }

  const handleQuickCreate = async () => {
    if (!qcForm.first_name.trim()) { toast.error('First name is required'); return }
    if (!workspace?.id) { toast.error('No workspace found — try refreshing'); return }
    setQcSaving(true)
    const { data, error } = await supabase
      .from('contacts')
      .insert({
        workspace_id: workspace.id,
        first_name: qcForm.first_name.trim(),
        last_name:  qcForm.last_name.trim()  || null,
        email:      qcForm.email.trim()      || null,
        company_name: qcForm.company_name.trim() || null,
      })
      .select('id, first_name, last_name, email, company_name')
      .single()
    setQcSaving(false)
    if (error) { toast.error(`Couldn't add customer: ${error.message}`); return }
    toast.success('Customer added')
    // Broad invalidation so every contact list across the app picks up the
    // new contact (Invoices typeahead, RecurringInvoices typeahead, the
    // Contacts page list, etc.). Prefix-match — queryKey ['contacts', …] all match.
    queryClient.invalidateQueries({ queryKey: ['contacts'] })
    // Auto-select the new contact into the invoice form so the operator
    // doesn't have to re-pick from the dropdown after the refetch lands.
    setForm(p => ({
      ...p,
      contact_id: data!.id,
      contact_name: `${data!.first_name} ${data!.last_name || ''}`.trim(),
      contact_email: data!.email || '',
      company_name: data!.company_name || '',
    }))
    // Collapse the panel + clear the qcForm.
    setShowQuickCreate(false)
    setQcForm({ first_name: '', last_name: '', email: '', company_name: '' })
  }

  const handleSave = async () => {
    // Require a real contacts(id). The manual-name fallback + sentinel write
    // were removed when this modal was hardened — the only path to set
    // contact_id is either the dropdown or the inline quick-create above.
    if (!form.contact_id) { toast.error('Please select a customer'); return }
    setSaving(true)
    // invoice_number is assigned by the assign_invoice_number BEFORE-INSERT
    // trigger (race-safe per-workspace counter, Phase A migration
    // 20260615000000). Omitting the key lets the trigger fire.
    const { data, error } = await supabase.from('invoices').insert({
      contact_id: form.contact_id,
      contact_name: form.contact_name,
      company_name: form.company_name || null,
      contact_email: form.contact_email,
      status: 'draft',
      line_items: [],
      subtotal: 0, discount_type: 'none', discount_value: 0,
      discount_amount: 0, tax_rate: 0, tax_amount: 0,
      total: 0, amount_paid: 0, amount_due: 0,
      currency: 'USD',
      issue_date: new Date().toISOString().split('T')[0],
      due_date: form.due_date || null,
      notes: form.notes,
      workspace_id: workspace!.id,
    }).select().single()
    setSaving(false)
    if (error) { toast.error(`Error: ${error.message}`); return }
    toast.success('Invoice created')
    onCreated(data.id)
    onClose()
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 24 }}
      onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.15 }}
        onClick={e => e.stopPropagation()}
        style={{ background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 20, padding: 28, width: '100%', maxWidth: 480, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexShrink: 0 }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', margin: 0 }}>New invoice</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0 }}><X style={{ width: 18, height: 18 }} /></button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto', minHeight: 0 }}>
          <div>
            <label style={lbl}>Customer *</label>
            <select value={form.contact_id} onChange={handleContactSelect} style={{ ...inp, cursor: 'pointer' }}>
              <option value="">Select a customer…</option>
              {contacts.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name || ''} {c.company_name ? `— ${c.company_name}` : ''}</option>)}
            </select>

            {/* Empty-contacts case messaging. The quick-create panel below
                still works if the feature/limit gates allow — so a brand-new
                workspace can create their first customer inline. If gates
                block, surface why. */}
            {contacts.length === 0 && (
              <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: '6px 0 0' }}>
                {!hasContactsFeature
                  ? 'Contacts aren’t included on your plan. Upgrade to add customers.'
                  : atLimit
                    ? `You’ve reached your plan’s contact limit${limit != null ? ` (${limit})` : ''}. Upgrade to add more.`
                    : 'No customers yet. Add one below.'}
              </p>
            )}

            {/* "+ Add new customer" toggle. Hidden when the feature/limit
                gates block (no side-door around the plan). Quick-create is
                also hidden once the panel is open (to avoid a redundant
                control). */}
            {canQuickCreate && !showQuickCreate && (
              <button type="button" onClick={() => setShowQuickCreate(true)}
                style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 8, padding: '4px 0', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent-text)', fontSize: 12, fontWeight: 600 }}>
                <UserPlus style={{ width: 13, height: 13 }} /> Add new customer
              </button>
            )}
            {!canQuickCreate && contacts.length > 0 && (
              <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: '6px 0 0' }}>
                {!hasContactsFeature
                  ? 'Quick-add unavailable on your plan.'
                  : `Contact limit reached${limit != null ? ` (${limit})` : ''}. Upgrade to add more.`}
              </p>
            )}
          </div>

          {/* Inline quick-create panel — minimal form. Full address/phone/etc.
              live on the Contacts page; this is the "I want to bill someone
              new RIGHT NOW" path. */}
          {showQuickCreate && (
            <div style={{ padding: '14px 14px 16px', background: 'var(--surface)', border: '1px solid var(--accent-border)', borderRadius: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <UserPlus style={{ width: 14, height: 14, color: 'var(--accent)' }} />
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-primary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>New customer</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div>
                  <label style={lbl}>First name *</label>
                  <input value={qcForm.first_name} onChange={e => qcSet('first_name', e.target.value)}
                    placeholder="Alex" style={inp} autoFocus
                    onFocus={e => e.currentTarget.style.borderColor='var(--focus-ring)'}
                    onBlur={e => e.currentTarget.style.borderColor='var(--border-default)'} />
                </div>
                <div>
                  <label style={lbl}>Last name</label>
                  <input value={qcForm.last_name} onChange={e => qcSet('last_name', e.target.value)}
                    placeholder="Johnson" style={inp}
                    onFocus={e => e.currentTarget.style.borderColor='var(--focus-ring)'}
                    onBlur={e => e.currentTarget.style.borderColor='var(--border-default)'} />
                </div>
              </div>
              <div>
                <label style={lbl}>Email</label>
                <input type="email" value={qcForm.email} onChange={e => qcSet('email', e.target.value)}
                  placeholder="alex@company.com" style={inp}
                  onFocus={e => e.currentTarget.style.borderColor='var(--focus-ring)'}
                  onBlur={e => e.currentTarget.style.borderColor='var(--border-default)'} />
              </div>
              <div>
                <label style={lbl}>Company</label>
                <input value={qcForm.company_name} onChange={e => qcSet('company_name', e.target.value)}
                  placeholder="Acme Inc." style={inp}
                  onFocus={e => e.currentTarget.style.borderColor='var(--focus-ring)'}
                  onBlur={e => e.currentTarget.style.borderColor='var(--border-default)'} />
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <button type="button"
                  onClick={() => { setShowQuickCreate(false); setQcForm({ first_name:'', last_name:'', email:'', company_name:'' }) }}
                  className="btn-ghost" style={{ flex: 1, justifyContent: 'center' }}>
                  Cancel
                </button>
                <button type="button" onClick={handleQuickCreate} disabled={qcSaving}
                  className="btn-primary" style={{ flex: 1, justifyContent: 'center' }}>
                  {qcSaving ? 'Adding…' : 'Add customer'}
                </button>
              </div>
            </div>
          )}
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Due date</label>
            {/* TODO Session 3: theme this — colorScheme: 'dark' pinned for native date picker icons */}
            <input type="date" value={form.due_date} onChange={e => set('due_date', e.target.value)} style={{ ...inp, colorScheme: 'dark' }} onFocus={e => e.currentTarget.style.borderColor='var(--focus-ring)'} onBlur={e => e.currentTarget.style.borderColor='var(--border-default)'} />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Notes</label>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Payment terms, notes..." rows={2}
              style={{ ...inp, resize: 'none', lineHeight: 1.5 }} onFocus={e => e.currentTarget.style.borderColor='var(--focus-ring)'} onBlur={e => e.currentTarget.style.borderColor='var(--border-default)'} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 24, flexShrink: 0 }}>
          <button onClick={onClose} className="btn-ghost" style={{ flex: 1, justifyContent: 'center' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} className="btn-primary" style={{ flex: 1, justifyContent: 'center' }}>{saving ? 'Creating...' : 'Create invoice'}</button>
        </div>
      </motion.div>
    </motion.div>
  )
}

export default function Invoices() {
  const { workspace } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const isMobile = useIsMobile()
  const [search, setSearch] = useState('')
  const [searchParams] = useSearchParams()
const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all')
  const [showNewModal, setShowNewModal] = useState(false)

  const { data: invoices = [], isLoading } = useQuery({
    queryKey: ['invoices', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data, error } = await supabase.from('invoices').select('*').eq('workspace_id', workspace.id).order('created_at', { ascending: false })
      if (error) throw error
      return data || []
    },
    enabled: !!workspace?.id,
  })

  const { data: contacts = [] } = useQuery({
    queryKey: ['contacts', 'typeahead-invoices', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('contacts').select('id, first_name, last_name, email, company_name').eq('workspace_id', workspace.id).order('first_name')
      return data || []
    },
    enabled: !!workspace?.id,
  })

  const metrics = useMemo(() => {
    const outstanding = invoices.filter((i: any) => ['sent','viewed','partial','overdue'].includes(i.status)).reduce((s: number, i: any) => s + (i.amount_due || 0), 0)
    const overdue = invoices.filter((i: any) => i.status === 'overdue').reduce((s: number, i: any) => s + (i.amount_due || 0), 0)
    const paid = invoices.filter((i: any) => i.status === 'paid').reduce((s: number, i: any) => s + (i.total || 0), 0)
    return { outstanding, overdue, paid, total: invoices.length }
  }, [invoices])

  const filtered = useMemo(() => {
    let list = [...invoices]
    if (statusFilter !== 'all') list = list.filter((i: any) => i.status === statusFilter)
    if (search) {
      const q = search.toLowerCase()
      list = list.filter((i: any) => i.invoice_number?.toLowerCase().includes(q) || i.contact_name?.toLowerCase().includes(q))
    }
    return list
  }, [invoices, statusFilter, search])

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--canvas)' }}>
      <div style={{ padding: '24px 28px 0', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 900, color: 'var(--ink-primary)', letterSpacing: '-0.03em', margin: 0 }}>Invoices</h1>
            <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: '3px 0 0' }}>{invoices.length} total</p>
          </div>
          <button onClick={() => setShowNewModal(true)} className="btn-primary">
            <Plus style={{ width: 15, height: 15 }} /> New invoice
          </button>
        </div>

        {/* Metrics — auto-fit so cards reflow on narrow viewports instead
            of compressing 4-up to unreadable widths on mobile. */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 20 }}>
          {[
            { label: 'Outstanding', value: formatCurrency(metrics.outstanding), color: 'var(--accent)', icon: DollarSign },
            { label: 'Overdue', value: formatCurrency(metrics.overdue), color: metrics.overdue > 0 ? 'var(--danger)' : 'var(--positive)', icon: AlertTriangle },
            { label: 'Collected', value: formatCurrency(metrics.paid), color: 'var(--positive)', icon: CheckCircle2 },
            { label: 'Total invoices', value: metrics.total, color: 'var(--info)', icon: FileText },
          ].map(m => (
            <div key={m.label} style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: '14px 16px', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: -20, right: -20, width: 60, height: 60, background: `radial-gradient(circle, color-mix(in srgb, ${m.color} var(--status-tint-fill), transparent) 0%, transparent 70%)`, pointerEvents: 'none' }} />
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 11, color: 'var(--ink-tertiary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{m.label}</span>
                <m.icon style={{ width: 14, height: 14, color: m.color }} />
              </div>
              <p style={{ fontSize: 20, fontWeight: 800, color: 'var(--ink-primary)', margin: 0, letterSpacing: '-0.02em' }}>{m.value}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16 }}>
          <div style={{ position: 'relative', flex: 1, maxWidth: 280 }}>
            <Search style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', width: 13, height: 13, color: 'var(--ink-tertiary)' }} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search invoices..."
              style={{ width: '100%', padding: '9px 12px 9px 32px', background: 'var(--field-bg)', border: '1px solid var(--border-subtle)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box' }}
              onFocus={e => e.currentTarget.style.borderColor='var(--focus-ring)'}
              onBlur={e => e.currentTarget.style.borderColor='var(--border-subtle)'} />
            {search && <button onClick={() => setSearch('')} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0 }}><X style={{ width: 12, height: 12 }} /></button>}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {['all', 'draft', 'sent', 'partial', 'paid', 'overdue', 'void'].map(s => (
              <button key={s} onClick={() => setStatusFilter(s)}
                style={{ padding: '7px 13px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize', background: statusFilter === s ? 'var(--accent)' : 'var(--hover-bg)', color: statusFilter === s ? 'var(--accent-ink)' : 'var(--ink-tertiary)', border: statusFilter === s ? 'none' : '1px solid var(--border-subtle)' }}>
                {s === 'all' ? 'All' : s}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Table */}
      <div style={{ flex: 1, overflow: 'auto', padding: '0 28px 28px' }}>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 14, overflow: 'hidden' }}>
          {/* Column header — desktop only. Cards on mobile are self-labeling. */}
          {!isMobile && (
          <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr 120px 120px 100px 80px', padding: '10px 16px', borderBottom: '1px solid var(--border-subtle)', background: 'var(--surface)' }}>
            {['Invoice', 'Customer', 'Amount', 'Due date', 'Status', ''].map((h, i) => (
              <div key={i} style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{h}</div>
            ))}
          </div>
          )}

          {isLoading ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-tertiary)', fontSize: 13 }}>Loading...</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: '60px 24px', textAlign: 'center' }}>
              <FileText style={{ width: 36, height: 36, color: 'var(--ink-tertiary)', margin: '0 auto 12px', display: 'block' }} />
              <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink-secondary)', margin: '0 0 6px' }}>
                {search || statusFilter !== 'all' ? 'No invoices match your filters' : 'No invoices yet'}
              </p>
              <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: '0 0 16px' }}>
                {search || statusFilter !== 'all' ? 'Try adjusting your filters' : 'Create your first invoice to start collecting payments'}
              </p>
              {!search && statusFilter === 'all' && (
                <button onClick={() => setShowNewModal(true)} className="btn-primary" style={{ margin: '0 auto' }}>
                  <Plus style={{ width: 14, height: 14 }} /> New invoice
                </button>
              )}
            </div>
          ) : (
            filtered.map((inv: any, i: number) => {
              const cfg = STATUS_CONFIG[inv.status] || STATUS_CONFIG.draft
              const isLast = i === filtered.length - 1

              // Mobile card. Customer + amount on the header row (the two
              // things the user actually scans for); invoice number kept
              // as a mono sub-line under customer (still searchable, still
              // visible, just de-emphasized). Status badge below amount.
              // Due date with the original overdue red coloring preserved.
              if (isMobile) {
                return (
                  <motion.div key={inv.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: Math.min(i * 0.02, 0.1) }}
                    onClick={() => navigate(`/app/invoices/${inv.id}`)}
                    style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '14px 16px', borderBottom: isLast ? 'none' : '1px solid var(--border-subtle)', cursor: 'pointer', transition: 'background 150ms ease' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-primary)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inv.contact_name}</p>
                        <p style={{ fontSize: 12, color: 'var(--accent)', fontFamily: 'monospace', margin: '2px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inv.invoice_number}</p>
                        {inv.company_name && <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: '2px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inv.company_name}</p>}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
                        <p style={{ fontSize: 16, fontWeight: 800, color: 'var(--ink-primary)', margin: 0, letterSpacing: '-0.01em' }}>{formatCurrency(inv.total)}</p>
                        {inv.amount_paid > 0 && inv.status !== 'paid' && <p style={{ fontSize: 11, color: 'var(--positive)', margin: 0 }}>{formatCurrency(inv.amount_paid)} paid</p>}
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 100, background: cfg.bg, color: cfg.color, textTransform: 'capitalize', whiteSpace: 'nowrap' }}>
                          {cfg.label}
                        </span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {inv.due_date && (
                        <span style={{ fontSize: 13, color: inv.status === 'overdue' ? 'var(--danger)' : 'var(--ink-secondary)', fontWeight: inv.status === 'overdue' ? 600 : 400 }}>
                          {inv.status === 'overdue' ? 'Overdue — was due ' : 'Due '}{formatDate(inv.due_date, 'MMM d, yyyy')}
                        </span>
                      )}
                      <span style={{ fontSize: 11, color: 'var(--ink-tertiary)' }}>
                        Created {formatRelativeDate(inv.created_at)}
                      </span>
                    </div>
                  </motion.div>
                )
              }

              // Desktop grid row — unchanged.
              return (
                <motion.div key={inv.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: Math.min(i * 0.02, 0.1) }}
                  onClick={() => navigate(`/app/invoices/${inv.id}`)}
                  style={{ display: 'grid', gridTemplateColumns: '140px 1fr 120px 120px 100px 80px', padding: '13px 16px', borderBottom: i < filtered.length - 1 ? '1px solid var(--border-subtle)' : 'none', cursor: 'pointer', transition: 'background 150ms ease', alignItems: 'center' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)', fontFamily: 'monospace' }}>{inv.invoice_number}</span>
                  <div>
                    <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)', margin: 0 }}>{inv.contact_name}</p>
                    {inv.company_name && <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: 0 }}>{inv.company_name}</p>}
                  </div>
                  <div>
                    <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-primary)', margin: 0 }}>{formatCurrency(inv.total)}</p>
                    {inv.amount_paid > 0 && inv.status !== 'paid' && <p style={{ fontSize: 11, color: 'var(--positive)', margin: 0 }}>{formatCurrency(inv.amount_paid)} paid</p>}
                  </div>
                  <span style={{ fontSize: 13, color: inv.status === 'overdue' ? 'var(--danger)' : 'var(--ink-secondary)' }}>
                    {inv.due_date ? formatDate(inv.due_date, 'MMM d, yyyy') : '—'}
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 100, background: cfg.bg, color: cfg.color, textTransform: 'capitalize', whiteSpace: 'nowrap' }}>
                    {cfg.label}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--ink-tertiary)', textAlign: 'right' }}>
                    {formatRelativeDate(inv.created_at)}
                  </span>
                </motion.div>
              )
            })
          )}
        </div>
      </div>

      <AnimatePresence>
        {showNewModal && (
          <NewInvoiceModal
            onClose={() => setShowNewModal(false)}
            onCreated={(id) => navigate(`/app/invoices/${id}`)}
            contacts={contacts}
          />
        )}
      </AnimatePresence>
    </div>
  )
}